import EventEmitter from './data/event-emitter';
import DataManager from './data/data-manager';
import {
    DATA_EVENTS,
    REQUEST_TIMEOUT,
    PULLING_FREQUENCY,
    GROUP_SYMBOL_QUANTITY,
    TOKEN_EXPIRED_TRIGGERED_MAX_COUNT
} from './data/constant';
import logger from './util/logger';
import url from './util/url';
import guid from './util/guid';
import { wrapPromise } from './util/promise-helper';
import axios from 'axios';

/**
 * @module marketsMarketData
 */
export default class MarketData extends EventEmitter {
    constructor(axiosInstance, migratoryDataClient) {
        super();
        this.util = {
            url,
            guid
        };
        this._isSetup = false;
        this._axiosInstance = axiosInstance;
        this._migratoryDataClient = migratoryDataClient;
        this._dataManager = new DataManager();
        this._dataManager.on(DATA_EVENTS.TOKEN_EXPIRED, (option, callback) => {
            this.emit(DATA_EVENTS.TOKEN_EXPIRED, option, callback);
        });
        this.ready = wrapPromise(async () => {
            const err = new Error(
                'Missing Instid, UsrID and ExchangeInfo from query permission'
            );
            const permission = await this._dataManager.permissions
                .query()
                .catch(() => {
                    throw err;
                });
            const { Instid: instid, UsrID: userId, exchangeInfo } = permission;
            if (instid && userId && exchangeInfo) {
                this._dataManager.setOption({
                    instid,
                    userId,
                    exchangeInfo
                });
                return permission;
            } else {
                throw err;
            }
        });
    }
    /**
     * Initialize the marketsMarketData module and it supported MaaS API gateway access token or markets session Id.
     * @function setup
     * @param {object} cfg - some configuration collection
     * @param {string} cfg.accessToken - An access token to be got with the MaaS token service.
     * @param {string} cfg.sessionId - A session id to be got with the markets authentication service. And it will be ignored if access token is passed.
     * @param {string} [cfg.env=stg] - The valid values are stg, uat or prod.
     * @param {number} [cfg.timeout=30000] - Set the global request timeout with milliseconds.
     * @param {number} [cfg.frequency=30000] - The value used to control the polling interval when using subscriber with milliseconds.
     * @param {number} [cfg.tokenExpiredTriggeredCount=5] - The value used to control upper limit the tokenExpired event triggered when the event is listened.
     * @param {string} [cfg.overridePaths] - The base path to be override.
     * @returns {object}  marketsMarketData
     * @example
     * var mktData = marketsMarketData.setup({
     *      accessToken: 'xxx',
     *      env: 'prod',
     *      timeout: 10000
     * });
     *
     * //with proxy
     * var mktData = marketsMarketData.setup({
     *      sessionId: 'xxx',
     *      env: 'prod',
     *      overridePaths: {
     *          '@web': 'https://proxy.example.com/markets/', // https://proxy.example.com/markets/ra/service ->  http://quotespeed.morningstar.com/ra/service
     *          '@pull': 'https://proxy.example.com/marketsPull/' // https://proxy.example.com/marketsPull/ra/service ->  http://pullqs.morningstar.com/pullservice/ra/service
     *      }
     * });
     *
     * // listen the tokenExpired event. The binding function will be fired when the token is expired.
     * mktData.on('tokenExpired', function(config, callback){
     *     // retrieve a new token (sessionId or accessToken) with a ajax then invoke callback
     *     $.get('http://api.example.com/tokenservice').then(function(token) {
     *        // if you are passing accessToken
     *        config.accessToken = token;
     *        // if you are passing sessionId
     *        config.sessionId = token;
     *        callback(config);
     *     });
     * });
     */
    setup({
        accessToken,
        sessionId,
        instid,
        env = 'prod',
        region = 'us',
        timeout = REQUEST_TIMEOUT,
        frequency = PULLING_FREQUENCY,
        tokenExpiredTriggeredCount = TOKEN_EXPIRED_TRIGGERED_MAX_COUNT,
        groupSymbolQuantity = GROUP_SYMBOL_QUANTITY,
        overridePaths
    } = {}) {
        if (!axios) {
            throw new Error('Missing required dependency axios');
        }
        if (!this._isSetup) {
            if (!accessToken && !sessionId) {
                throw new Error('Missing required accessToken or sessionId');
            }
            if (!this._axiosInstance) {
                this._axiosInstance = axios.create();
            }
            this._dataManager.setOption({
                env,
                region,
                timeout,
                accessToken,
                sessionId,
                instid,
                tokenExpiredTriggeredCount,
                groupSymbolQuantity,
                overridePaths,
                frequency,
                axiosInstance: this._axiosInstance,
                migratoryDataClient: this._migratoryDataClient
            });
            this._isSetup = true;
        } else {
            logger.info(
                '[marketsMarketData] ',
                'return instance directly that it is setup already',
                {
                    env,
                    region,
                    timeout,
                    accessToken,
                    sessionId,
                    instid,
                    tokenExpiredTriggeredCount,
                    groupSymbolQuantity,
                    overridePaths,
                    frequency
                }
            );
        }
        return this;
    }

    /**
     * Get security instance array with given symbols
     * @function securities
     * @param {array} symbols - An array of symbol. The symbol could be performance id, security id, exch:symbol, realtime id.
     * @returns {Promise}
     * @fulfil {array} - The security instance array
     * @reject {Error} - The error
     *
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.securities(['126.1.IBM','XNAS:MSFT','0P000000GY']).then(function(securities) {
     *     console.log('securities', securities);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async securities(symbols) {
        await this.ready();
        return this._dataManager.securityManager.create(symbols);
    }

    /**
     * get securitiesByFormData
     * @function securitiesByFormData
     * @param {object} params - some parameters collection
     * @param {FormData} params.formData - upload file.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.securitiesByFormData({
     *     formData: new FormData()
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async securitiesByFormData(formData) {
        await this.ready();
        return this._dataManager.securityManager.createByFormData(formData);
    }

    /**
     * Get the pull subscriber
     * @function pull
     * @returns {Promise}
     * @fulfil {object} - The subscriber object, Please see {@link #subscriber} for further information
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.pull().then(function(subscriber) {
     *     console.log('subscriber', subscriber);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async pull() {
        await this.ready();
        return this._dataManager.connectPull();
    }

    /**
     * Get the stream subscriber
     * @function stream
     * @returns {Promise}
     * @fulfil {object} - The subscriber object, Please see {@link #subscriber} for further information
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.stream().then(function(subscriber) {
     *     console.log('subscriber', subscriber);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async stream() {
        await this.ready();
        return this._dataManager.connectStream();
    }

    /**
     * Get snapshot quote data
     * @function quotes
     * @param {object} params - some parameters collection
     * @param {array} params.securities - An array of security. The security could be performance id, security id, exch:symbol, realtime id ..
     * @param {string} [params.method=get] - Request type. Value is 'get' or 'post'.
     * @param {number} [params.timeout=30000] - Set the request timeout with milliseconds.
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     *
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.quotes({
     *     securities: ['126.1.IBM', 'XNAS:MSFT', '0P000000GY'],
     * }).then(function(data) {
     *     console.log('quotes', data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async quotes(params) {
        await this.ready();
        const pull = await this._dataManager.connectPull();
        return pull.quotes(params);
    }
    /**
     * Get gainer, loser and most actives entitled tickers
     * @function statisticsTickers
     * @param {object} params - some parameters collection
     * @param {number} [params.timeout=30000] - Set the request timeout with milliseconds.
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.statisticsTickers().then(function(data) {
     *     console.log('statistics tickers', data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async statisticsTickers(params) {
        await this.ready();
        const pull = await this._dataManager.connectPull();
        return pull.statisticsTickers(params);
    }

    /**
     * Get gainer, loser and most actives of the market exchange
     * @function statistics
     * @param {object} params - some parameters collection
     * @param {array} params.instruments - An array of realtime instrument such as 126.1.MSTATS_ALL,127.1.MSTATS_ALL and so on.
     * @param {string} params.statFields - The statistics fields(More than one are connected by comma). Valid values are D2156,D2157,D2158,D2159,D2160.
     * @param {string} [params.quoteFields] - The extra quote fields(More than one are connected by comma) such as S3377,S12,D18,D19.
     * @param {number} [params.timeout=30000] - Set the request timeout with milliseconds.
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.statistics({
     *     instruments: ['126.1.MSTATS_ALL', '127.1.MSTATS_ALL'],
     *     statFields: 'D2160,D2158',
     *     quoteFields: 'S3377,D18,D19'
     * }).then(function(data) {
     *     console.log('statistics', data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async statistics(params) {
        await this.ready();
        const pull = await this._dataManager.connectPull();
        return pull.statistics(params);
    }

    /**
     * Get intraday/EOD price/nav data.
     * @function timeSeries
     * @param {object} params - some parameters collection
     * @param {string} [params.query] - Used to specify the data points of each security.
     * @param {frequency} params.frequency - required. Optional values are 1, 10, 15, 30, 60, 'd', 'w', 'm'.
     * @param {string} params.startDate - Start date of the timeseries. Format is YYYY-MM-DDThh-mm-ss.
     * @param {string} params.endDate - End date of the timeseries. Format is YYYY-MM-DDThh-mm-ss.
     * @param {number} [params.tradingDays] - Used to specify the number of trading days when request intraday timeseries data without using the start time and end time. It works when frequency is set to 1, 10, 15, 30 or 60. Max 60 trading days supported
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.timeSeries({
     *      query: '126.1.IBM,19.1.MSFT:OPEN,HIGH,LOW,CLOSE,VOLUME',
     *      dataPoints:['open','close','high','low'],
     *      tradingDays: 2,
     *      frequency: 10
     * }).then(function(data) {
     *      console.log('intraday timeseries data', data);
     * }).catch(function(error) {
     *      console.log(error);
     * });
     * mktData.timeSeries({
     *      query: '126.1.IBM,19.1.MSFT:OPEN,HIGH,LOW,CLOSE,VOLUME|52.8.FARIX:TOTALRETURN,NAV',
     *      start: '20160418',
     *      end: '20170425',
     *      frequency: 'd'
     * }).then(function(data) {
     *      console.log('historical timeseries data', data);
     * }).catch(function(error) {
     *      console.log(error);
     * });
     */
    async timeSeries(params) {
        await this.ready();
        return this._dataManager.timeSeries.timeSeries(params);
    }

    /**
     * Export timeSeries data to excel.
     * @function exportTimeSeries
     * @param {object} params - some parameters collection
     * @param {string} params.fileName - The excel file name.
     * @param {object} params.summary - The security summary information
     * @param {array} params.sheets - required. The excel sheets data.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.exportTimeSeries({
     *    fileName: 'IBM',
     *    sheets: [{
     *            id: 'securitiesData',
     *            text: 'Price',
     *            data: [{
     *                    securityLabel: 'IBM',
     *                    dataPoints: [{
     *                            id: 'date',
     *                            dataType: 'date',
     *                            text: 'Date'
     *                        }, {
     *                            id: 'close',
     *                            dataType: 'number',
     *                            decimal: 2,
     *                            text: 'Close'
     *                        }, {
     *                            id: 'volume',
     *                            dataType: 'integer',
     *                            text: 'Volume'
     *                        }
     *                    ],
     *                    data: [{
     *                            date: '2022-01-07',
     *                            close: 134.83,
     *                            volume: 5238099
     *                        }, {
     *                            date: '2022-01-10',
     *                            close: 135.03,
     *                            volume: 5432807
     *                        }
     *                    ]
     *                }
     *            ]
     *        }
     *    ],
     *    summary: {
     *        security: {
     *            value: 'IBM',
     *            text: 'Security'
     *        },
     *        startDate: {
     *            value: '2022-01-07',
     *            text: 'Start Date'
     *        },
     *        endDate: {
     *            value: '2022-02-07',
     *            text: 'End Date'
     *        }
     *    }
     * }).then(function(data) {
     *      console.log('export success', data);
     * }).catch(function(error) {
     *      console.log(error);
     * });
     */
    async exportTimeSeries(params) {
        await this.ready();
        return this._dataManager.timeSeries.exportData(params);
    }

    /**
     * Get static data
     * @function staticData
     * @param {object} params - some parameters collection
     * @param {array} params.securities - An array of security. The security could be performance id, security id, exch:symbol, realtime id
     * @param {string} [params.method=get] - Request type. Value is 'get' or 'post'.
     * @param {number} [params.timeout=30000] - Set the request timeout with milliseconds.
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.staticData({
     *     securities: ['126.1.IBM', 'XNAS:MSFT', '0P000000GY']
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.error(error);
     * });
     */
    async staticData(params) {
        await this.ready();
        return this._dataManager.staticData.staticData(params);
    }

    /**
     * Get security details data
     * @function securityDetails
     * @param {object} params - some parameters collection
     * @param {array} params.securities - An array of security. The security could be performance id, security id, exch:symbol, realtime id
     * @param {string} [params.method=get] - Request type. Value is 'get' or 'post'.
     * @param {number} [params.timeout=30000] - Set the request timeout with milliseconds.
     * @param {string} [params.fileds] - Get the security static data fields key.
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.securityDetails({
     *     securities: ['126.1.IBM', 'XNAS:MSFT', '0P000000GY'],
     *     fields: 'ST299,OS463,AR111'
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.error(error);
     * });
     */
    async securityDetails(params) {
        await this.ready();
        return this._dataManager.securityDetails.securityDetails(params);
    }

    /**
     * Get option chain data
     * @function optionChain
     * @param {object} params - some parameters collection
     * @param {string} params.security - A valid security could be performance id, security id, exch:symbol, realtime id
     * @returns {Promise}
     * @fulfil {object} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.optionChain({
     *     security: '126.1.IBM'
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     *
     */
    async optionChain(params) {
        await this.ready();
        return this._dataManager.options.chain(params);
    }

    /**
     * Get option greeks
     * @function optionGreeks
     * @param {object} params - some parameters collection
     * @param {array} params.instruments - Options realtime instrument array, values can get from optionChain response(instrument key in options array)
     * @param {string} params.rootInstrument - One stock realtime instrument, can get from data.instrument(optionChain response)
     * @returns {Promise}
     * @fulfil {object} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.optionGreeks({
     *     instruments: ['126.2.IBM8BGC080000', '126.2.IBM8C9C142000'],
     *     rootInstrument: '126.1.IBM'
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async optionGreeks(params) {
        await this.ready();
        return this._dataManager.options.greeks(params);
    }

    /**
     * get treasury bond
     * @function treasuryBond
     * @param {object} params - some parameters collection
     * @param {string} [params.sdate] - start time
     * @param {string} [params.edate] - end time
     * @param {Array}  [params.timePeriods] supported time period 1/3/6 Month, 1/2/3/5/7/10/20/30 Year
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     *
     * // get snapshot for all supported time periods
     * mktData.treasuryBond().then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     *
     * // get custom date range timeseris for specified time periods
     * mktData.treasuryBond({
     *     sdate: '20151228',
     *     edate: '20161230',
     *     timePeriods: ['1 Month', '1 Year']
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     *
     */
    async treasuryBond(params = {}) {
        await this.ready();
        return this._dataManager.bonds.treasury(params);
    }

    /**
     * get us government bond
     * @function usGovernmentBond
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     *
     * // get snapshot
     * mktData.usGovernmentBond().then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async usGovernmentBond() {
        await this.ready();
        return this._dataManager.bonds.government();
    }

    /*eslint-disable*/
    /**
     * get news headline list
     * @function newsHeadlines
     * @param {object} params - some parameters collection
     * @param {string} [params.productCode = QS] - A unique id that generated by news team. You can contact news team to get your one if you are morningstar internal.
     * @param {string} [params.timezone = America/Chicago] - Target timeZone that want to transform the publish time
     * @param {string} [params.idType=ES] - Symbol format that valid value is ES, CS or SC.
     * @param {string} [params.symbols] - Symbol connected with comma. Format should be exchange:symbol(idType=ES), country:symbol(idType=CS) or performanceId(idType=SC).
     * @param {string} [params.sourceCode] - Source code list connected by comma.
     * @param {string} [params.sourceType=All] - Source type list connected by comma.
     * @param {string} [params.providerCode] - Provider code list connected by comma.
     * @param {string} [params.vendor] - Vendor list connected by comma.
     * @param {string} [params.fromDate] - Format yyyy-mm-dd or yyyy-mm-ddTHH:MM:SS.
     * @param {string} [params.toDate] - Format yyyy-mm-dd or yyyy-mm-ddTHH:MM:SS.
     * @param {number} [params.maxCount=100] - Used to control the number of top news.
     * @param {number} [params.startAt] - The start index to get news.
     * @param {number} [params.endAt] - The end index to get news.
     * @param {string} [params.lang=EN] - Related language news that values could be EN, CN or FR.
     * @param {string} [params.srchType=k] - Work together with keywords that value could be t(title query), c(content query), k(keywords query).
     * @param {string} [params.keywords] - Keywords generally has category codes, article keywords etc. For And operation, seperate the keywords with +. Ex: If we are searching for articles that have keywords IBM, Annual, Report, the keyword query string will be IBM+Annual+Report. For OR operation, seperate the keyword with +OR+.  Ex: If we are searching for articles that have one of more of the keywords IBM, Annual, Report, the keyword query string will be IBM+OR+Annual+OR+Report. A,B,C means A OR B OR C.
     * @param {boolean} [params.withLogo=false] - Control if return the logo image that value could be true or false.
     * @param {string} [params.logSize=small] - Control the logo image size that value could be small or medium.
     * @param {string} [params.method=get] - Request type. Value is 'get' or 'post'.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.newsHeadlines({
     *     symbols: 'XNYS:IBM'
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    /*eslint-enable*/
    async newsHeadlines(params = {}) {
        await this.ready();
        return this._dataManager.news.headlines(params);
    }

    /**
     * get news view with resourceId
     * @function newsView
     * @param {object} params - some parameters collection
     * @param {string} [params.productCode = QS] - A unique id that generated by news team. You can contact news team to get your one if you are morningstar internal.
     * @param {string} [params.timezone = America/Chicago] - Target timeZone that want to transform the publish time
     * @param {string} params.resourceId - The resourceId that could be got from newsHeadlines method.
     * @param {boolean} [params.withLogo=false] - Control if return the logo image that value could be true or false.
     * @param {string} [params.logSize=small] - Control the logo image size that value could be small or medium.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.newsView({
     *     resourceId: 'BenzBZPR_11126293'
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async newsView(params) {
        await this.ready();
        return this._dataManager.news.view(params);
    }

    /**
     * get most viewed news
     * @function newsMostViewed
     * @param {object} params - some parameters collection
     * @param {string} [params.productCode = QS] - A unique id that generated by news team. You can contact news team to get your one if you are morningstar internal.
     * @param {string} [params.timezone = America/Chicago] - Target timeZone that want to transform the publish time
     * @param {number} [params.maxCount=100] - Used to control the number of top view news.
     * @param {number} [params.recentDays] - Used to control  the top view news in latest days.
     * @param {boolean} [params.withLogo=false] - Control if return the logo image that value could be true or false.
     * @param {string} [params.logSize=small] - Control the logo image size that value could be small or medium.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.newsMostViewed().then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async newsMostViewed(params = {}) {
        await this.ready();
        return this._dataManager.news.mostViewed(params);
    }

    /**
     * get logo image base64 code
     * @function newsLogos
     * @param {object} params - some parameters collection
     * @param {string} [params.productCode = QS] - A unique id that generated by news team. You can contact news team to get your one if you are morningstar internal.
     * @param {Array} params.ids - An array of id(Vendor:ProviderCode:ServiceCode)
     * @param {string} [params.size=small] - Used to control the logo image size that value could be samll or medium.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.newsLogos({
     *     ids: ['Morningstar:MSTAR:ETFII', 'Zacks:ZACKS:ZIR']
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async newsLogos(params = {}) {
        await this.ready();
        return this._dataManager.news.logos(
            params.productCode,
            params.ids,
            params.size
        );
    }

    /**
     * get source code list
     * @function newsSources
     * @param {object} params - some parameters collection
     * @param {string} [params.productCode = QS] - A unique id that generated by news team. You can contact news team to get your one if you are morningstar internal.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.newsSources().then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async newsSources(params = {}) {
        await this.ready();
        return this._dataManager.news.sources(
            params.productCode,
            params.vendor,
            params.sourceType
        );
    }

    /**
     * get news vendor list
     * @function newsVendors
     * @param {object} params - some parameters collection
     * @param {string} [params.productCode = QS] - Provided by morningstar news team that it is required only for morningstar internal team.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.newsVendors().then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async newsVendors(params = {}) {
        await this.ready();
        return this._dataManager.news.vendors(params.productCode);
    }

    async track(params) {
        await this.ready();
        return this._dataManager.track.send(params);
    }

    async queryPermission(params) {
        await this.ready();
        return this._dataManager.permissions.query(params);
    }

    /**
     * get autocomplete
     * @function autocomplete
     * @param {object} params - some parameters collection
     * @param {string} params.kw - search keyword.
     * @param {string} params.id - unique id that will mark the service return.
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.autocomplete({
     *     kw: 'ib',
     *     id: '1234'
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async autocomplete(params) {
        await this.ready();
        return this._dataManager.autocomplete.query(params);
    }

    getOption() {
        return this._dataManager.getOption();
    }

    /**
     * get timeSales
     * @function timeSales
     * @param {object} params - some parameters collection
     * @param {string} params.security - A valid security could be performance id, security id, exch:symbol, realtime id
     * @param {string} [params.count = 200] - count of the return trades
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.timeSales({
     *     security: '126.1.IBM',
     *     count: 200
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.log(error);
     * });
     */
    async timeSales(params) {
        await this.ready();
        return this._dataManager.timeSales.getTimeSales(params);
    }
    /**
     * get active alerts
     * @function activeAlerts
     * @param {object} params - some parameters collection
     * @param {number} params.start -  The start index to get active alerts.
     * @param {number} params.count - The count of the return alerts
     * @param {number} [params.order=desc] - The order based on update time, optional values are 'desc', 'asc'
     * @returns {Promise}
     * @example
        mktData.activeAlerts({
            count: 20,
            start: 0
        }).then(function(data) {
            console.log('activeAlerts', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async activeAlerts(params) {
        await this.ready();
        return this._dataManager.alert.activeAlerts(params);
    }

    /**
     * get videos
     * @function getVideos
     * @param {object} params - some parameters collection
     * @param {String} params.categories -  The start index to get active videos.
     * @param {number} params.count - The count of the return videos
     * @param {number} params.start - The count of the return videos
     * @returns {Promise}
     * @example
        mktData.getVideos({
            categories: 'EQUITY',
            start: 0,
            count: 20
        }).then(function(data) {
            console.log('getVideos', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async getVideos(params) {
        await this.ready();
        return this._dataManager.videos.getVideos(params);
    }

    /**
     * get recurringAlerts alerts
     * @function recurringAlerts
     * @param {object} params - some parameters collection
     * @param {number} params.start - required. The start index to get recurring alerts.
     * @param {number} params.count - required. The count of the return alerts
     * @param {string} [params.order=desc] - The order based on update time, optional values are 'desc', 'asc'
     * @returns {Promise}
     * @example
        mktData.recurringAlerts({
            count: 20,
            start: 0
        }).then(function(data) {
            console.log('recurringAlerts', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async recurringAlerts(params) {
        await this.ready();
        return this._dataManager.alert.recurringAlerts(params);
    }
    /**
      * get triggered alerts
      * @function triggeredAlerts
      * @param {object} params - some parameters collection
      * @param {number} params.start - required. The start index to get recurring alerts.
      * @param {number} params.count - required. The count of the return alerts
      * @param {boolean} params.readStatus - To filter unread or read triggered alerts.
      * @param {string} [params.order=desc] - The order based on update time, optional values are 'desc', 'asc'
      * @param {string} params.startDate - Start date of the triggered alerts. Format is yyyy-MM-dd.
      * @param {string} params.endDate - End date of the triggered alerts. Format is yyyy-MM-dd.
      * @returns {Promise}
      * @example
         mktData.triggeredAlerts({
             count: 20,
             start: 0,
             startDate:'2019-04-01',
             endDate:'2019-05-01'
         }).then(function(data) {
             console.log('triggeredAlerts', data);
         }).catch(function(error) {
             console.log(error);
         });
     */
    async triggeredAlerts(params) {
        await this.ready();
        return this._dataManager.alert.triggeredAlerts(params);
    }
    /**
     * subscribe alert
     * @function subscribeAlert
     * @param {object} params - some parameters collection
     * @param {number} params.alertID - required. The alert type 1~113.
     * @param {number} params.instrument - required. Only support realtime symbol like 126.1.IBM.
     * @param {number} params.targetValue - user target value.
     * @param {number} params.change - required when alertID is 17 or 19.
     * @param {boolean} params.autoReset - only works when alertID is 1, 2, 3, 4, 5, 6, 7, 8, 21, 27, 28
     * @returns {Promise}
     * @example
        mktData.subscribeAlert({
            alertID: '19',
            change: 90.8,
            targetValue: 100,
            instrument: '126.1.IBM'
        }).then(function(data) {
            console.log('subscribeAlert', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async subscribeAlert(params) {
        await this.ready();
        return this._dataManager.alert.subscribeAlert(params);
    }

    /**
     * update alert
     * @function updateAlert
     * @param {object} params - some parameters collection
     * @param {number} params.uniqueAlertID - required. The unique alert Id.
     * @param {number} params.alertID - required. The alert type 1~113.
     * @param {number} params.instrument - required. Only support realtime symbol like 126.1.IBM.
     * @param {number} params.targetValue - user target value.
     * @param {number} params.change - required when alertID is 17 or 19.
     * @param {boolean} params.autoReset - only works when alertID is 1, 2, 3, 4, 5, 6, 7, 8, 21, 27, 28
     * @returns {Promise}
     * @example
        mktData.updateAlert({
            uniqueAlertID: "301041900011",
            targetPrice:"180",
            change:"70",
            alertID:"19",
            value:180,
            instrument:"126.1.IBM"
        }).then(function(data) {
            console.log('updateAlert', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async updateAlert(params) {
        await this.ready();
        return this._dataManager.alert.updateAlert(params);
    }

    /**
     * delete alerts
     * @function deleteAlert
     * @param {object} params - some parameters collection
     * @param {string} params.uniqueAlertID - required. The unique alert Id, use ',' between alert ids for multiple alerts.     * @returns {Promise}
     * @returns {Promise}
     * @example
       mktData.deleteAlert({
            uniqueAlertID: "301041900006,301041900011"
        }).then(function(data) {
            console.log('deleteAlert', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async deleteAlert(params) {
        await this.ready();
        return this._dataManager.alert.deleteAlert(params);
    }
    /**
     * save config
     * @function saveAlertConfig
     * @param {object} params - some parameters collection
     * @param {string} params.email - User email address.
     * @param {string} params.lang - language, default is en.
     * @param {string} params.sendStatus - Control if send email to the email address.
     * @returns {Promise}
     * @example
        mktData.saveAlertConfig({
            lang: "en",
            sendStatus: true
        }).then(function(data){
            console.log('saveAlertConfig', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async saveAlertConfig(params) {
        await this.ready();
        return this._dataManager.alert.saveAlertConfig(params);
    }
    /**
     * get config
     * @function getAlertConfig
     * @returns {Promise}
     * @example
        mktData.getAlertConfig().then(function(data){
            console.log('saveAlertConfig', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async getAlertConfig(params) {
        await this.ready();
        return this._dataManager.alert.getAlertConfig(params);
    }
    /**
     * acknowledge alerts
     * @function acknowledgeAlerts
     * @param {object} params - some parameters collection
     * @param {string} params.uniqueAlertID - Unique alert ID. Use ',' between alert ids for multiple alerts, use 'all' to acknowledge all unread alerts.
     * @returns {Promise}
     * @example
         mktData.acknowledgeAlerts({
            uniqueAlertID: "319041900001"
        }).then(function(data){
            console.log('acknowledgeAlerts', data);
        }).catch(function(error) {
            console.log(error);
        });
    */
    async acknowledgeAlerts(params) {
        await this.ready();
        return this._dataManager.alert.acknowledgeAlerts(params);
    }

    /**
     * get research bullsVsBears
     * @function getResearchBullsVsBears
     * @param params - some parameters collection, you can use security.
     * @param params.security - A valid security could be performance id, security id, exch:symbol, realtime id.
     * @returns {Promise}
     * @example
        mktData.getResearchBullsVsBears({
            security: "126.1.IBM"
        }).then(function(data){
            console.log('getResearchBullsVsBears', data);
        }).catch(function(error) {
            console.log(error);
        });
     */
    async getResearchBullsVsBears(params) {
        await this.ready();
        return this._dataManager.research.getResearchBullsVsBears(params);
    }

    /**
     * get research highlights
     * @function getResearchHighlights
     * @param params - some parameters collection, you can use security.
     * @param params.security - A valid security could be performance id, security id, exch:symbol, realtime id.
     * @returns {Promise}
     * @example
        mktData.getResearchHighlights({
            security: "126.1.IBM"
        }).then(function(data){
            console.log('getResearchHighlights', data);
        }).catch(function(error) {
            console.log(error);
        });
     */
    async getResearchHighlights(params) {
        await this.ready();
        return this._dataManager.research.getResearchHighlights(params);
    }

    /**
     * get research competitors
     * @function getResearchCompetitors
     * @param params - some parameters collection, you can use security.
     * @param params.security - A valid security could be performance id, security id, exch:symbol, realtime id.
     * @returns {Promise}
     * @example
     mktData.getResearchCompetitors({
            security: "126.1.IBM"
        }).then(function(data){
            console.log('getResearchCompetitors', data);
        }).catch(function(error) {
            console.log(error);
        });
     */
    async getResearchCompetitors(params) {
        await this.ready();
        return this._dataManager.research.getResearchCompetitors(params);
    }

    /**
     * get regulatory filings document list
     * @function getRegulatoryFilings
     * @param params - some parameters collection, you can use security.
     * @param params.performanceIds - A valid performanceId could be performance id
     * @returns {Promise}
     * @example
     mktData.getRegulatoryFilings({
            performanceIds: ["0P00002D7Z"]
        }).then(function(data){
            console.log('getRegulatoryFilings', data);
        }).catch(function(error) {
            console.log(error);
        });
     */
    async getRegulatoryFilings(params) {
        await this.ready();
        return this._dataManager.documents.regulatoryFilings(params);
    }
    /**
     * transform the data to be exported
     * @function exportWatchlist
     * @param params - the request parameter
     * @param params.columnLabel - the column header label
     * @param params.columns - the column key
     * @param params.fileType - the export file type, such as excel
     * @param params.sheetName - the excel sheet name
     * @param params.data - the data to be exported, cplumns split by '|', rows split by '||'
     * @returns {Promise}
     */
    async exportWatchlist(params) {
        await this.ready();
        return this._dataManager.download.exportWatchlist(params);
    }
    /**
     * export the file to ExcelRTD
     * @function exportExcelRTD
     * @param params - the request parameter
     * @param params.symbols - the symbols to be watched in ExcelRTD
     * @param params.sheetName - the sheet name in excel
     * @returns {Promise}
     */
    async exportExcelRTD(params) {
        await this.ready();
        return this._dataManager.download.exportExcelRTD(params);
    }
    /**
     * Get static data
     * @function snapshot
     * @param {object} params - some parameters collection
     * @param {array} params.securities - An array of security. The security could be performance id, security id, exch:symbol, realtime id
     * @param {array} params.fields - An array of data points.
     * @param {string} [params.method=get] - Request type. Value is 'get' or 'post'.
     * @param {number} [params.timeout=30000] - Set the request timeout with milliseconds.
     *
     * @returns {Promise}
     * @fulfil {array} - The data collection json
     * @reject {Error} - The error
     * @example
     * var mktData = marketsMarketData.setup({...});
     * mktData.snapshot({
     *     securities: ['126.1.IBM', 'XNAS:MSFT', '0P000000GY'],
     *     fields: ['navLastTradeDate', 'priceLastTradeDate', 'ctLastTradeDate']
     * }).then(function(data) {
     *     console.log(data);
     * }).catch(function(error) {
     *     console.error(error);
     * });
     */
    async snapshot(params) {
        await this.ready();
        return this._dataManager.snapshot.snapshot(params);
    }

    /**
     * get translated names for fund, category or benchmark
     * @function getName
     * @param params - some parameters collection.
     * @param {string} params.language - Language id format like'en_us'.
     * @param {array} params.fund - Security id of fund
     * @param {array} params.category - category id
     * @param {array} params.benchmark - benchmark id
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.getName({
            language: "en_us",
            fund: ["F00000UMMA"],
            category: ["PGSZB4TTTT"],
            benchmark: ["F00000JZ87"]
        }).then(function(data){
            console.log('getName', data);
        }).catch(function(error) {
            console.log(error);
        });
     */
    async getName(params) {
        await this.ready();
        return this._dataManager.getName.getName(params);
    }

    /**
     * Get saved drawings
     * @function getDrawings
     * @param params - some parameters collection.
     * @param {string} params.secId - Security id.
     * @param {string} params.eventType - Event type defined in data base. Default is 'ch_v4_draw'. Multiple event types split with ','
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.getDrawings({
            secId: '0P000002RH'
        }).then(function(data){
            console.log('Drawings', data);
        });
     */
    async getDrawings(params) {
        await this.ready();
        return this._dataManager.userProfile.getDrawings(params);
    }

    /**
     * Save drawings
     * @function saveDrawings
     * @param params - some parameters collection.
     * @param {string} params.secId - Security id.
     * @param {array} params.drawings - Drawings to save
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.saveDrawings({
            secId: '0P000002RH',
            drawings: [{
            "start": {
                "date": "2022-03-28T12:00:00",
                "yValue": 174.59
            },
            "end": {
                "date": "2022-03-28T12:57:00",
                "yValue": 173.
            },
            "id": "default-2",
            "drawType": "rectangle"
        }]}).then(function(data){
            console.log(data);
        });
     */
    async saveDrawings(params) {
        await this.ready();
        return this._dataManager.userProfile.saveDrawings(params);
    }

    /**
     * Remove drawings
     * @function removeDrawing
     * @param params - some parameters collection.
     * @param {string} params.drawId - Security id. Multiple drawing split with ','
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.removeDrawings({
            drawId: '41844'
        }).then(function(data){
            console.log('Drawings', data);
        });
     */
    async removeDrawings(params) {
        await this.ready();
        return this._dataManager.userProfile.removeDrawings(params);
    }

    /**
     * Update drawings
     * @function updateDrawings
     * @param params - some parameters collection.
     * @param {string} params.secId - Security id.
     * @param {string} params.drawId - Draw id
     * @param {array} params.info - Drawing information to update
     * @returns {Promise}
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.saveDrawings({
            secId: '0P000002RH',
            drawId: '41844'
            info: {
            "start": {
                "date": "2022-03-28T12:00:00",
                "yValue": 174.59
            },
            "end": {
                "date": "2022-03-28T12:57:00",
                "yValue": 173.
            },
            "id": "default-2",
            "drawType": "rectangle"
        }}).then(function(data){
            console.log(data);
        });
     */
    async updateDrawings(params) {
        await this.ready();
        return this._dataManager.userProfile.updateDrawings(params);
    }

    /**
     * Get User Profile
     * @function getUserProfile
     * @param params - some parameters collection.
     * @param {string} params.types - Profile type defined in data base. Multiple event types split with ','
     * @returns {Promise} 
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.getUserProfile({
            types: 'ch_v3,ch_v4'
        }).then(function(data){
            console.log('UserProfile', data);
        });
     */
    async getUserProfile(params) {
        await this.ready();
        return this._dataManager.userProfile.getUserProfile(params);
    }

    /**
     * Get User Profile
     * @function saveUserProfile
     * @param params - some parameters collection.
     * @param {number} params.id - Profile id, not required if insert a new profile
     * @param {string} params.type - Profile type, like 'ch_v4'
     * @param {string} params.profile - Profile Json string.
     * @returns {Promise} 
     * @example
     * var mktData = marketsMarketData.setup({...});
        mktData.saveUserProfile({ 
            id: 7511, 
            type: 'ch_v4', 
            profile: {
                savedConfig: {
                    symbols: ['126.1.IBM']
                }
            } 
        }).then(function(res){
            console.log(res);
        });
     */
    async saveUserProfile(params) {
        await this.ready();
        return this._dataManager.userProfile.saveUserProfile(params);
    }
}
